/**
 * Legacy transcript hook — active on desktop (Tauri) and on web when
 * `release-live-transcripts` is off. Endpoint: `GET /sessions/:id/transcript`.
 * Pairs with `useQueryLiveTranscriptBySessionId` (the v2 live hook — app-side
 * for now: it is the capture boundary, see the D2 scope note in
 * `openspec/changes/monorepo-session-data-core/tasks.md`).
 *
 * Moved from `src/hooks/query/useQueryTranscriptBySessionId.ts` (which now
 * delegates here) with two inputs hoisted to parameters, the W3c pattern —
 * a data package may not read the Rematch store or the build profile:
 *   - `isRecording` — was `useSelector(state.timerModel.isRunning)`
 *   - `isOnPrem`    — was the `@/utils/buildProfile` module constant
 * The app-side delegate supplies both, so old-path callers are unchanged.
 */
import { logger } from '@heidi/runtime/logger';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { GET_TRANSCRIPT_BY_SESSION_ID } from '../../contracts';
import { getSessionTranscript } from '../api/transcription';
import { getSessionDataConfig } from '../config';

/** Mirrors `src/hooks/query/constants.ts` — moves properly with a later family. */
const MAX_QUERY_RETRIES = 3;

/** Fetch transcript, returning null for 404 (transcript not yet available). */
async function fetchTranscript(sessionId: string) {
  try {
    const { data } = await getSessionTranscript(sessionId);
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

const ONPREM_TRANSCRIPT_POLL_INTERVAL_MS = 5000;

/**
 * On-prem has no live-transcript stream and no chronicle-sync invalidation (the
 * desktop refetch path), so the transcript only refreshes when this query is
 * refetched. Polling rules (on-prem only; returns `false` elsewhere, leaving
 * cloud/desktop behaviour unchanged):
 * - While recording is in progress (including after a pause/resume), poll so
 *   newly transcribed chunks surface. Without this, polling would stop the
 *   moment the first transcript lands and resumed chunks would never show until
 *   a manual reload.
 * - When not recording, poll only until the transcript first lands (catches the
 *   transcription that completes shortly after upload/stop), then stop.
 */
export const getTranscriptRefetchInterval = (
  data: Awaited<ReturnType<typeof fetchTranscript>> | undefined,
  isRecording = false,
  isOnPrem = false
): number | false => {
  if (!isOnPrem) return false;
  if (isRecording) return ONPREM_TRANSCRIPT_POLL_INTERVAL_MS;
  return !data?.transcript ? ONPREM_TRANSCRIPT_POLL_INTERVAL_MS : false;
};

export const preFetchTranscripts = async (sessionId: string) => {
  await getSessionDataConfig()
    .getQueryClient()
    .prefetchQuery({
      queryKey: [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
      queryFn: () => fetchTranscript(sessionId),
    });
};

/**
 * Marks the legacy transcript query stale. Mounted observers (notably
 * `useQueryTranscriptBySessionId` in `TranscriptsTabPane_LEGACY`) refetch
 * automatically — `enabled` is gated only on `sessionId`, so the hook
 * subscription always triggers a refetch when the URL has a sessionId.
 * Unmounted observers pick up fresh data on next mount.
 */
export async function invalidateTranscriptBySessionId(sessionId: string) {
  if (!sessionId) return;
  try {
    await getSessionDataConfig()
      .getQueryClient()
      .invalidateQueries({
        queryKey: [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
      });
  } catch (error) {
    logger.warn({
      msg: '[desktop_remote_transcript]: invalidate failed',
      sessionId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Do not retry on 4xx errors (e.g. 404 transcript not found) */
const shouldRetry = (failureCount: number, error: Error) => {
  if (
    isAxiosError(error) &&
    error.response &&
    error.response.status >= 400 &&
    error.response.status < 500
  ) {
    return false;
  }
  return failureCount < MAX_QUERY_RETRIES;
};

export function useQueryTranscriptBySessionId(
  sessionId: string,
  {
    isRecording = false,
    isOnPrem = false,
  }: { isRecording?: boolean; isOnPrem?: boolean } = {}
) {
  return useQuery({
    queryKey: [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
    enabled: !!sessionId,
    retry: shouldRetry,
    queryFn: () => fetchTranscript(sessionId),
    refetchInterval: (query) =>
      getTranscriptRefetchInterval(query.state.data, isRecording, isOnPrem),
  });
}
